import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@clerk/react'
import { useQuery } from '@tanstack/react-query'
import { Sun, MoonStar, RotateCw } from 'lucide-react'
import { PageWrapper } from '../components/ui/PageWrapper'
import { BetaFeedbackBanner } from '../components/BetaFeedbackBanner'
import { BetaCompNotice } from '../components/BetaCompNotice'
import { PullLimitBadge, usePullLimits } from '../components/ui/PullLimitBadge'
import { InstallCard } from '../components/ui/InstallCard'
import { pullsGreeting } from '../lib/freeReadings'
import { useUserStore } from '../stores/userStore'
import { apiFetch } from '../services/api'
import { getProfileGaps, type ProfileGapField } from '../lib/profileCompletion'
import { getEffectiveTier } from '../lib/tier'
import { ceremonyChipLabel } from '../lib/countdown'
import { DECK_REGISTRY } from '@tarrot/shared'

interface DailyAstro {
  sunSign: string
  moonSign: string
  moonPhase: string
  moonIllumination: number
  retrogrades: string[]
  dailyEnergy: string
}

interface Ceremony {
  id: string
  title: string
  description: string | null
  scheduledAt: string
  isPaidOnly: boolean
  hasAccess: boolean
  isRsvpd: boolean
  platform: string
  spotsLeft: number | null
}

/** Truthful one-word status for the banner — never claims a booking or an
 * open spot that isn't real. */
function ceremonyStatusLabel(c: Ceremony): string {
  if (c.isRsvpd) return 'RSVP’d'
  if (!c.hasAccess) return 'Cosmic only'
  if (c.platform === 'youtube') return 'Open to all'
  if (c.spotsLeft === 0) return 'Full'
  return 'Spots open'
}

type AstroState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: DailyAstro }
  | { kind: 'empty' }
  | { kind: 'error' }

type CeremonyState =
  | { kind: 'loading' }
  | { kind: 'ready'; ceremony: Ceremony | null }
  | { kind: 'error' }

const SKELETON_TIMEOUT_MS = 8000

function readDismissed(key: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

function writeDismissed(key: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(key, 'true')
  } catch {
    /* swallow quota or private-mode errors silently */
  }
}

/** Short phase name from the API's "Waxing Gibbous"-style strings. */
function shortenPhase(phase: string | undefined): string {
  if (!phase) return ''
  // Preserve compound phase names like "Waxing Crescent", "Last Quarter"
  // so the chip text is meaningful, not just "Crescent" / "Quarter".
  return phase
}

interface RecentReading {
  id: string
  question: string | null
  createdAt: string
  deckId: string
  cards: Array<{ cardId: string; orientation: string }>
}

function readingDateLabel(iso: string): string {
  const d = new Date(iso)
  return `${d.toLocaleDateString('en-US', { weekday: 'short' })} · ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
}

/** Tarot card art is public; oracle art is auth-gated (would 404 here), so
 * oracle readings get the shared card back instead of a hidden broken img. */
function readingThumbSrc(deckId: string, cardId: string): string {
  const deck = DECK_REGISTRY.find((d) => d.id === deckId)
  if (deck && (!deck.cardKind || deck.cardKind === 'tarot')) return `${deck.cardImagePrefix}${cardId}.webp`
  return '/cards/card-back.webp'
}

export function HomePage() {
  const { isSignedIn, isLoaded: authLoaded } = useAuth()
  const profile = useUserStore((s) => s.profile)
  const navigate = useNavigate()

  // React Query: dedupes across remounts within a 1-hour window and
  // keeps cached data across route changes (gcTime in main.tsx). The
  // /astro/daily response also carries Cache-Control headers so a fresh
  // tab reuses the browser's HTTP cache before even hitting React Query.
  const astroQuery = useQuery<DailyAstro>({
    queryKey: ['astro', 'daily'],
    queryFn: async ({ signal }) => {
      const r = await apiFetch('/astro/daily', { signal })
      if (!r.ok) throw new Error(`astro/daily ${r.status}`)
      return r.json()
    },
    // Daily astro is stable for ~an hour (backend recomputes once per day);
    // this stays consistent with the HTTP Cache-Control on the response.
    staleTime: 60 * 60 * 1000,
  })

  const ceremoniesQuery = useQuery<{ ceremonies?: Ceremony[] }>({
    queryKey: ['ceremonies', 'list'],
    queryFn: async ({ signal }) => {
      const r = await apiFetch('/ceremonies', { signal })
      if (!r.ok) throw new Error(`ceremonies ${r.status}`)
      return r.json()
    },
    // Ceremony list changes rarely (Sarah schedules monthly); stay quiet
    // for 10 min before considering a refetch.
    staleTime: 10 * 60 * 1000,
  })

  const readingsQuery = useQuery<{ readings?: RecentReading[] }>({
    queryKey: ['readings', 'recent'],
    enabled: !!isSignedIn,
    queryFn: async ({ signal }) => {
      const r = await apiFetch('/readings', { signal })
      if (!r.ok) throw new Error(`readings ${r.status}`)
      return r.json()
    },
    staleTime: 5 * 60 * 1000,
  })
  // The strip renders real history only — hidden entirely until there is some.
  const recentReadings = (readingsQuery.data?.readings ?? [])
    .filter((r) => r.cards.length > 0)
    .slice(0, 3)

  // Skeleton-timeout failsafe: if a fetch hasn't resolved by
  // SKELETON_TIMEOUT_MS, promote the surface to its error state so we
  // never sit on a perpetual skeleton on a flaky connection. React Query
  // tracks `fetchStatus === 'fetching'`; we layer a setTimeout to bound it.
  const [astroFailsafe, setAstroFailsafe] = useState(false)
  const [ceremoniesFailsafe, setCeremoniesFailsafe] = useState(false)
  useEffect(() => {
    if (!astroQuery.isPending) { setAstroFailsafe(false); return }
    const id = setTimeout(() => setAstroFailsafe(true), SKELETON_TIMEOUT_MS)
    return () => clearTimeout(id)
  }, [astroQuery.isPending])
  useEffect(() => {
    if (!ceremoniesQuery.isPending) { setCeremoniesFailsafe(false); return }
    const id = setTimeout(() => setCeremoniesFailsafe(true), SKELETON_TIMEOUT_MS)
    return () => clearTimeout(id)
  }, [ceremoniesQuery.isPending])

  // Re-render once a minute so the ceremony banner countdown stays live.
  // Minute resolution is enough for a "Xd Yh" / "Xh Ym" chip, and avoids the
  // per-second churn the UpcomingCeremonyCard needs for its finer ticker.
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])

  // Derive the existing discriminated union from useQuery's primitives so
  // the consumer JSX (which switches on `astroState.kind`) doesn't change.
  const astroState: AstroState =
    astroQuery.isSuccess
      ? (astroQuery.data?.dailyEnergy ? { kind: 'ready', data: astroQuery.data } : { kind: 'empty' })
      : astroQuery.isError || astroFailsafe
        ? { kind: 'error' }
        : { kind: 'loading' }

  const ceremonyState: CeremonyState =
    ceremoniesQuery.isSuccess
      ? { kind: 'ready', ceremony: (ceremoniesQuery.data?.ceremonies ?? [])[0] ?? null }
      : ceremoniesQuery.isError || ceremoniesFailsafe
        ? { kind: 'error' }
        : { kind: 'loading' }

  const [onboardingDismissed, setOnboardingDismissed] = useState(() =>
    readDismissed('cosmic-finder:onboarding-dismissed'),
  )
  const [birthDataDismissed, setBirthDataDismissed] = useState(() =>
    readDismissed('cosmic-finder:birth-data-dismissed'),
  )
  const [betaWelcomeDismissed, setBetaWelcomeDismissed] = useState(() =>
    readDismissed('cosmic-finder:beta-welcome-dismissed'),
  )
  const [questionnaireFixNoticeDismissed, setQuestionnaireFixNoticeDismissed] = useState(() =>
    readDismissed('cosmic-finder:questionnaire-restored-notice-dismissed'),
  )

  // Profile is "ready" once Clerk knows the user is signed out OR Clerk is loaded
  // and our profile fetch completed. Used to gate banner decisions so they don't
  // flash in then out as profile resolves.
  const profileReady = authLoaded && (!isSignedIn || profile !== null)

  // Observability: log fetch failures once. Replaces the per-request
  // console.warn calls from the old useEffect-based fetcher.
  useEffect(() => {
    if (astroQuery.error) console.warn('[HomePage] /astro/daily fetch failed', astroQuery.error)
  }, [astroQuery.error])
  useEffect(() => {
    if (ceremoniesQuery.error) console.warn('[HomePage] /ceremonies fetch failed', ceremoniesQuery.error)
  }, [ceremoniesQuery.error])

  const firstName = profile?.displayName?.split(' ')[0] ?? 'friend'

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

  const astro = astroState.kind === 'ready' ? astroState.data : null
  const mercuryRetro = astro?.retrogrades?.some((r) => r.includes('Mercury')) ?? false
  const ceremony = ceremonyState.kind === 'ready' ? ceremonyState.ceremony : null

  // Banner queue: birth-data > onboarding > beta-feedback. One at a time.
  // Hidden ones defer until next mount; no auto-promotion in same session.
  //
  // birth-data is now the unified "missing astro context" nudge — covers free
  // and paid users, and fires on any of {birthday, location, time}. Birthday
  // gaps route to /onboarding (the onboarding flow is the natural place to add
  // it); time/location gaps route to /profile?edit=birth and auto-open the
  // edit form. `birthTimeSkipped` is respected so users who said "I don't
  // know" aren't pestered again.
  const profileGaps = getProfileGaps(profile)
  const onboardingEligible =
    profileReady && isSignedIn && profile && !profile.onboardingCompletedAt && !onboardingDismissed
  const birthDataEligible =
    profileReady && isSignedIn && profile && profileGaps.anyMissing && !birthDataDismissed

  let activeBanner: 'birth-data' | 'onboarding' | 'beta' = 'beta'
  if (birthDataEligible) activeBanner = 'birth-data'
  else if (onboardingEligible) activeBanner = 'onboarding'

  const birthDataCopy: Record<ProfileGapField, { body: string; ctaLabel: string; href: string }> = {
    birthday: {
      body: 'Help the sky meet you. Add your birthday so your readings can speak to who you are.',
      ctaLabel: 'Add your birthday →',
      href: '/onboarding',
    },
    location: {
      body: 'Where were you born? Your birth location lets your chart find the houses and angles that are uniquely yours.',
      ctaLabel: 'Add birth location →',
      href: '/profile?edit=birth#birth-info',
    },
    time: {
      body: 'One more detail for deeper readings — your birth time sharpens your moon and rising signs. If you don’t know it, you can mark it as unknown.',
      ctaLabel: 'Add birth time →',
      href: '/profile?edit=birth#birth-info',
    },
  }
  const activeBirthDataCopy = profileGaps.primaryGap ? birthDataCopy[profileGaps.primaryGap] : null

  // Used by the error-state "try again" button. React Query handles the
  // network call + cache invalidation; we just trigger a refetch.
  const retryAstro = () => {
    astroQuery.refetch()
    ceremoniesQuery.refetch()
  }

  const effectiveTier = profile ? getEffectiveTier(profile) : 'free'
  const isPaid = effectiveTier === 'paid'
  const pullLimits = usePullLimits()

  return (
    <PageWrapper>
      <div className="w-full pb-20">
        {/* ── Preserved: Beta welcome card ──
            One-time greeting for testers, sits above the banner queue.
            Dismisses to localStorage; once dismissed it doesn't return. */}
        {profileReady && isSignedIn && profile?.role === 'beta_tester' && !betaWelcomeDismissed && (
          <div
            data-testid="beta-welcome-card"
            className="relative bg-cosmic-card-warm rounded-xl p-4 pr-10 border border-gold/30"
          >
            <button
              type="button"
              onClick={() => {
                setBetaWelcomeDismissed(true)
                writeDismissed('cosmic-finder:beta-welcome-dismissed')
              }}
              aria-label="Dismiss welcome"
              className="absolute top-1.5 right-1.5 p-2 -m-1 text-text-secondary hover:text-text-primary leading-none text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar rounded"
            >
              ×
            </button>
            <p className="text-xs tracking-cosmic-wider uppercase text-gold/80 font-medium mb-2">
              Thank you for walking this in with us
            </p>
            <p className="text-sm text-text-primary/80 font-display italic leading-relaxed mb-3">
              You're one of the first to step inside The Cosmic Finder. This is a digital altar — a place to sit with a card, write what surfaces, and listen to what the sky is saying today.
            </p>
            <ul className="text-xs text-text-secondary font-body leading-relaxed space-y-1.5 mb-3">
              <li>
                <span className="text-gold/80 font-medium">Pull a card.</span> Then write in the journal. That's the practice.
              </li>
              <li>
                <span className="text-gold/80 font-medium">Switch perspectives.</span> In your profile, toggle between free and Cosmic views — payments are paused during beta, so try both.
              </li>
              <li>
                <span className="text-gold/80 font-medium">Tell us what you notice.</span> Use the questionnaire on your profile, or text Sarah anything that catches your eye.
              </li>
            </ul>
            <button
              type="button"
              onClick={() => {
                setBetaWelcomeDismissed(true)
                writeDismissed('cosmic-finder:beta-welcome-dismissed')
              }}
              className="text-xs tracking-cosmic uppercase text-gold font-medium hover:text-gold-light transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar rounded-sm"
            >
              Got it →
            </button>
          </div>
        )}

        {/* ── Preserved: Questionnaire-restored notice ──
            One-time notice for beta testers whose questionnaire button was
            landing on the "closed" state due to a missing prod feature flag.
            Shows independently of the welcome card so testers who already
            dismissed welcome still see the fix announcement. */}
        {profileReady && isSignedIn && profile?.role === 'beta_tester' && !questionnaireFixNoticeDismissed && (
          <div
            data-testid="questionnaire-restored-notice"
            className="relative bg-cosmic-surface rounded-xl p-4 pr-10 border-l-2 border-gold/40 mt-3"
          >
            <button
              type="button"
              onClick={() => {
                setQuestionnaireFixNoticeDismissed(true)
                writeDismissed('cosmic-finder:questionnaire-restored-notice-dismissed')
              }}
              aria-label="Dismiss notice"
              className="absolute top-1.5 right-1.5 p-2 -m-1 text-text-secondary hover:text-text-primary leading-none text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar rounded"
            >
              ×
            </button>
            <p className="font-display italic text-sm text-text-primary leading-relaxed mb-2">
              Apologies — the questionnaire was unavailable for a few days. It's working again now, and your reflections would still mean the world.
            </p>
            <button
              type="button"
              onClick={() => {
                setQuestionnaireFixNoticeDismissed(true)
                writeDismissed('cosmic-finder:questionnaire-restored-notice-dismissed')
                navigate('/beta-feedback')
              }}
              className="text-xs tracking-cosmic uppercase text-gold font-medium hover:text-gold-light transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar rounded-sm"
            >
              Open the questionnaire →
            </button>
          </div>
        )}

        <BetaCompNotice />

        {/* ── Preserved: Banner queue (single, priority-queued) ──
            birth-data > onboarding > beta. One at a time. */}
        {activeBanner === 'birth-data' && birthDataEligible && activeBirthDataCopy && (
          <DismissibleBanner
            tone="gold"
            body={activeBirthDataCopy.body}
            ctaLabel={activeBirthDataCopy.ctaLabel}
            onCta={() => navigate(activeBirthDataCopy.href)}
            onDismiss={() => {
              setBirthDataDismissed(true)
              writeDismissed('cosmic-finder:birth-data-dismissed')
            }}
          />
        )}
        {activeBanner === 'onboarding' && onboardingEligible && (
          <DismissibleBanner
            tone="violet"
            body="Personalize your experience. Add your birth data to unlock your natal chart and personalized readings."
            ctaLabel="Complete setup →"
            onCta={() => navigate('/onboarding')}
            onDismiss={() => {
              setOnboardingDismissed(true)
              writeDismissed('cosmic-finder:onboarding-dismissed')
            }}
          />
        )}
        {activeBanner === 'beta' && <BetaFeedbackBanner />}

        {/* ── Install invitation ── quiet, dismissible; only where there's
            actually something to offer (native prompt or iOS manual path). */}
        {isSignedIn && <InstallCard />}

        {/* ── A. Greeting block ── */}
        <div className="pt-4">
          <p className="text-[0.7rem] tracking-[0.2em] uppercase text-text-secondary">
            {today}
          </p>
          <h1 className="font-display text-3xl font-light leading-tight mt-3 text-text-primary">
            Welcome back, <em className="italic text-accent-fg">{firstName}</em>
          </h1>
          <p className="text-sm leading-relaxed mt-3 text-text-secondary max-w-[440px]">
            {isPaid
              ? 'The full deck is yours today. Pull whenever you need clarity.'
              : pullLimits.loaded
                ? pullsGreeting(pullLimits.limit, pullLimits.remaining)
                : '\u00A0' /* hold the line height; a wrong-count flash reads as a glitch */}
          </p>
        </div>

        {/* Desktop grid wrapper: left = energy card, right = pull limit + ceremony + astro */}
        <div className="lg:grid lg:grid-cols-[1.5fr_1fr] lg:gap-5">
          {/* ── B. Today's Energy featured card ── */}
          <section
            aria-labelledby="today-energy-heading"
            className="mt-6 bg-bg-card border border-accent-border rounded-2xl p-5 shadow-[0_0_20px_var(--color-accent-muted)]"
          >
            {/* Top row: eyebrow + moon phase pill */}
            <div className="flex justify-between items-start gap-3">
              <h2
                id="today-energy-heading"
                className="text-[0.7rem] tracking-[0.2em] uppercase text-text-secondary"
              >
                Today's energy
              </h2>
              {astro?.moonPhase && (
                <span className="inline-flex px-3 py-1 rounded-full bg-violet-bg border border-violet-border text-astral-violet text-[0.6rem] tracking-[0.2em] uppercase whitespace-nowrap">
                  {shortenPhase(astro.moonPhase)}
                </span>
              )}
            </div>

            {/* Italic Sarah-voice quote — the only sentence, not duplicated */}
            {astroState.kind === 'ready' && (
              <p className="mt-4 font-display italic text-xl leading-snug text-accent-fg">
                {astroState.data.dailyEnergy}
              </p>
            )}

            {astroState.kind === 'loading' && (
              <div className="mt-4 space-y-2.5" aria-hidden="true">
                <div className="h-4 rounded-sm bg-text-primary/8 animate-pulse" />
                <div className="h-4 rounded-sm bg-text-primary/8 w-[95%] animate-pulse" />
                <div className="h-4 rounded-sm bg-text-primary/8 w-[88%] animate-pulse" />
                <div className="h-4 rounded-sm bg-text-primary/8 w-[70%] animate-pulse" />
              </div>
            )}

            {astroState.kind === 'empty' && (
              <p className="mt-4 font-display italic text-text-secondary leading-relaxed">
                Today's reading is being prepared. Check back in a moment.
              </p>
            )}

            {astroState.kind === 'error' && (
              <div className="mt-4">
                <p className="font-display italic text-text-secondary leading-relaxed">
                  Couldn't reach today's sky.
                </p>
                <button
                  type="button"
                  onClick={retryAstro}
                  className="mt-1 font-display italic text-gold hover:text-gold-light transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar rounded-sm"
                >
                  try again →
                </button>
              </div>
            )}

            {/* Two CTA buttons row */}
            {astroState.kind !== 'error' && (
              <div className="mt-5 flex gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => navigate('/find-clarity')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-bg border border-accent-border text-accent-fg text-sm tracking-wide"
                >
                  Pull a card
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/find-direction')}
                  className="text-sm text-text-secondary inline-flex items-center gap-1"
                >
                  Read the moon →
                </button>
              </div>
            )}
          </section>

          {/* Right column: Pull limit + Tonight's ceremony + Astro weather */}
          <div className="flex flex-col gap-4 mt-4 lg:mt-6">
            {/* ── C. Pull limit card ── */}
            <div className="bg-bg-surface border border-[color:var(--hairline)] rounded-xl p-4 flex justify-between items-center gap-3 flex-wrap">
              <PullLimitBadge />
              {!isPaid && (
                <button
                  type="button"
                  onClick={() => navigate('/upgrade?from=spreads')}
                  className="text-sm text-accent-fg hover:text-accent-fg/80 transition-colors"
                >
                  Upgrade to Cosmic →
                </button>
              )}
            </div>

            {/* ── D. Tonight's ceremony banner ── */}
            {ceremonyState.kind === 'loading' && (
              <div
                aria-hidden="true"
                className="bg-bg-warm border border-rose-border rounded-xl p-4 space-y-2.5"
              >
                <div className="h-3 w-32 rounded-sm bg-text-primary/8 animate-pulse" />
                <div className="h-5 w-48 rounded-sm bg-text-primary/8 animate-pulse" />
                <div className="h-3 w-36 rounded-sm bg-text-primary/8 animate-pulse" />
              </div>
            )}
            {ceremony && (
              <div
                role="button"
                tabIndex={0}
                onClick={() => navigate('/find-events')}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/find-events')}
                className="bg-bg-warm border border-rose-border rounded-xl p-4 flex gap-3 items-center cursor-pointer"
              >
                <span
                  aria-hidden="true"
                  className="w-2 h-2 flex-shrink-0 rounded-full bg-rose shadow-[0_0_10px_var(--color-rose)]"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[0.6rem] tracking-[0.2em] uppercase text-rose font-medium">
                    {ceremonyChipLabel(new Date(ceremony.scheduledAt).getTime(), nowMs)}
                  </p>
                  <p className="font-display text-lg leading-snug mt-0.5 text-text-primary">
                    {ceremony.title}
                  </p>
                  <p className="text-xs text-text-secondary mt-0.5">
                    {/* TODO(home): ceremony.hostName not in API shape yet — using Sarah as fallback */}
                    {/* "RSVP'd" only when actually booked — hasAccess is true for every
                        paid member and used to falsely reassure them they were booked. */}
                    Sarah · {new Date(ceremony.scheduledAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} · {ceremonyStatusLabel(ceremony)}
                  </p>
                </div>
                <span className="text-text-secondary text-lg leading-none flex-shrink-0">›</span>
              </div>
            )}

            {/* ── E. Astrological weather ──
                Phase chip intentionally dropped (audit + critique fix: the chip
                made the strip read as a SaaS stat block). */}
            {astroState.kind === 'ready' && astro && (
              <div className="bg-bg-surface border border-[color:var(--hairline)] rounded-xl p-4">
                <p className="text-[0.7rem] tracking-[0.2em] uppercase text-text-secondary mb-3">
                  Astrological weather
                </p>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Sun size={14} strokeWidth={1.5} />
                    <span>Sun in {astro.sunSign}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <MoonStar size={14} strokeWidth={1.5} />
                    <span>Moon in {astro.moonSign}</span>
                  </div>
                  <div className={`flex items-center gap-2 text-sm ${mercuryRetro ? 'text-rose-light' : 'text-text-secondary'}`}>
                    <RotateCw size={14} strokeWidth={1.5} />
                    <span>Mercury {mercuryRetro ? 'retrograde' : 'direct'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── F. Recent readings strip ── real history only; hidden until
            the member has pulled at least one card. */}
        {recentReadings.length > 0 && (
          <div className="mt-6 mb-20">
            <div className="flex justify-between items-center mb-3">
              <p className="text-[0.7rem] tracking-[0.2em] uppercase text-text-secondary">
                Recent readings
              </p>
              <button
                type="button"
                onClick={() => navigate('/journal')}
                className="text-sm text-accent-fg"
              >
                See all →
              </button>
            </div>
            <div className="flex flex-col gap-3 lg:grid lg:grid-cols-3">
              {recentReadings.map((r) => (
                <div
                  key={r.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate('/journal')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate('/journal')
                    }
                  }}
                  className="bg-bg-surface border border-[color:var(--hairline)] rounded-xl p-3 flex gap-3 items-center cursor-pointer"
                >
                  <img
                    src={readingThumbSrc(r.deckId, r.cards[0].cardId)}
                    alt=""
                    className="w-8 rounded-sm object-cover border border-gold/20 flex-shrink-0"
                    style={r.cards[0].orientation === 'reversed' ? { transform: 'rotate(180deg)' } : undefined}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[0.6rem] tracking-[0.2em] uppercase text-text-secondary">
                      {readingDateLabel(r.createdAt)}
                    </p>
                    {r.question ? (
                      <p className="font-display italic text-sm mt-1 text-text-primary leading-snug">
                        "{r.question}"
                      </p>
                    ) : (
                      <p className="font-body text-sm mt-1 text-text-secondary leading-snug">
                        An open pull
                      </p>
                    )}
                    <p className="text-xs text-text-muted mt-0.5">
                      {r.cards.length === 1
                        ? `1 card · ${r.cards[0].orientation}`
                        : `${r.cards.length} cards`}
                    </p>
                  </div>
                  <span className="text-text-secondary text-lg leading-none flex-shrink-0">›</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PageWrapper>
  )
}

/* ────────────────────────────── components ─────────────────────────── */

interface DismissibleBannerProps {
  tone: 'gold' | 'violet'
  body: string
  ctaLabel: string
  onCta: () => void
  onDismiss: () => void
}

function DismissibleBanner({ tone, body, ctaLabel, onCta, onDismiss }: DismissibleBannerProps) {
  const borderClass = tone === 'gold' ? 'border-gold/25' : 'border-astral-violet/25'
  const ctaTone =
    tone === 'gold'
      ? 'text-gold-light hover:text-gold transition-colors'
      : 'text-astral-violet-light hover:text-astral-violet transition-colors'

  return (
    <div
      className={`relative mt-3 bg-cosmic-card-warm rounded-xl p-3.5 pr-10 border ${borderClass}`}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="absolute top-1.5 right-1.5 p-2 -m-1 text-text-secondary hover:text-text-primary leading-none text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar rounded"
      >
        ×
      </button>
      <p className="text-sm text-text-primary font-display leading-relaxed">{body}</p>
      <button
        type="button"
        onClick={onCta}
        className={`mt-2 text-xs tracking-cosmic uppercase font-medium ${ctaTone} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-astral-violet focus-visible:ring-offset-2 focus-visible:ring-offset-cosmic-topbar rounded-sm`}
      >
        {ctaLabel}
      </button>
    </div>
  )
}
